<template>
  <section
    class="c-py-5 c-lg:c-py-6 overflow-hidden"
    :class="{ 'mb-6': isAdventCalendar }"
  >
    <div class="container">
      <h2 class="c-mb-5 c-lg:c-mb-6 text-center">
        {{ isAdventCalendar ? t('more_about_calendar') : t('read_more_articles') }}
      </h2>
      <div class="row no-gutters ml-n2 c-md:ml-n3 c-lg:ml-n4 c-xl:ml-n5 c-xxl:ml-n7">
        <!-- Mobile -->
        <Swiper
          v-if="BrowserService.isTablet()"
          id="js-similar-blog-posts"
          class="lightSlider--center row no-gutters c-mx-0 w-100 c-mb-0 c-flex-nowrap c-flex-grow-1 h-auto list-unstyled"
          slides-per-view="auto"
          :scrollbar="{ draggable: true, hide: false }"
        >
          <!-- blog item -->
          <SwiperSlide
            v-for="article in articles"
            :key="article.id"
            class="col-11 c-sm:col-8 c-md:col-6 c-lg:col-4 flex c-flex-col c-pl-2 c-md:c-pl-3 c-lg:c-pl-4 c-xl:c-pl-5 c-xxl:c-pl-7"
          >
            <article class="shadowbox c-p-3 c-sm:c-p-4 flex c-flex-col c-flex-grow-1 relative c-rounded-lg">
              <div class="embed-responsive embed-responsive-4by3 c-mb-4">
                <RemoteResponsiveImage
                  :product-image-data="article.image"
                  img-class="embed-responsive-item image-cover"
                  :alt="article.title"
                  sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
                />
              </div>

              <h3 class="c-mb-4 c-leading-base text-lg-c font-family-base font-medium">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="stretched-link !no-underline"
                >
                  {{ article.title }}
                </router-link>
              </h3>
              <p class="c-mb-5 text-sm-c">
                {{ article.perex }}
              </p>
              <div class="c-mt-auto text-center">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="btn btn-outline-primary c-px-5 stretched-link c-rounded-pill font-medium"
                >
                  {{ t('read_full_article') }}
                </router-link>
              </div>
            </article>
          </SwiperSlide>
        </Swiper>
        <!-- Desktop -->
        <ul
          v-else
          class="row c-mx-0 w-100 c-mb-0 c-flex-nowrap c-lg:c-flex-wrap c-flex-grow-1 h-auto no-gutters c-mb-0 list-unstyled"
        >
          <li
            v-for="article in articles"
            :key="article.id"
            class="col-6 c-sm:col-5 c-md:col-4 flex c-flex-col c-pl-2 c-md:c-pl-3 c-lg:c-pt-3 c-xl:c-pl-4 c-xl:c-pt-4"
          >
            <article class="shadowbox c-p-3 c-sm:c-p-4 flex c-flex-col c-flex-grow-1 relative c-rounded-lg">
              <RemoteResponsiveImage
                :product-image-data="article.image"
                img-class="w-100 c-mb-4"
                :alt="article.title"
                sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
              />

              <h3 class="c-mb-4 c-leading-base text-lg-c font-family-base font-medium">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="stretched-link !no-underline"
                >
                  {{ article.title }}
                </router-link>
              </h3>
              <p class="c-mb-5 text-sm-c">
                {{ article.perex }}
              </p>
              <div class="c-mt-auto text-center">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="btn btn-outline-primary c-px-5 stretched-link c-rounded-pill font-medium"
                >
                  {{ t('read_full_article') }}
                </router-link>
              </div>
            </article>
          </li>
        </ul>
      </div>
      <div
        v-if="!isAdventCalendar"
        class="c-mt-7 text-center"
      >
        <router-link
          :to="{ name: 'blogPath' }"
          class="btn btn-success  c-px-5 c-py-3 c-rounded-pill font-medium"
        >
          {{ t('show_all') }}
        </router-link>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import SwiperCore from 'swiper'
import { Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import BrowserService from '@/services/browserService'
import type { Article, Articles } from '@/types/articles'

SwiperCore.use([Scrollbar])

const props = defineProps({
  isAdventCalendar: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n()
const articles = ref<Article[]>([])
const swiper = ref()

const adventArticles = [
  t('advent_article_1'),
  t('advent_article_2'),
  t('advent_article_3'),
];

const initPage = async () => {
  if (!props.isAdventCalendar) {
    const { data } = await RetailApiV2.get('articles?limit=3')
    articles.value = (data as Articles).articles
  } else {
    articles.value.push(
      ...(await Promise.all(
        adventArticles.map(async (article) => (await RetailApiV2.get(`articles/preview/${article}`)).data.article),
      )),
    );
  }

  if (BrowserService.isTablet()) {
    // @ts-expect-error we know #js-similar-blog-posts is a swiper
    swiper.value = document.querySelector('#js-similar-blog-posts').swiper
  }
  if (swiper.value) {
    setTimeout(() => {
      swiper.value.scrollbar?.updateSize()
    }, 1000)
  }
}
initPage()
</script>

<i18n>
  {
    "cs": {
    "advent_article_1": "adventni-kalendar-mixit",
    "advent_article_2": "15-duvodu-proc-adventni-kalendar",
    "advent_article_3": "velky-adventni-kalendar",
    "more_about_calendar": "Více o adventním kalendáři",
    "read_full_article": "Číst celý článek",
    "read_more_articles": "Číst další články",
    "show_all": "Zobrazit všechny"
},
    "sk": {
    "advent_article_1": "adventny-kalendar-2025",
    "advent_article_2": "15-dovodov-preco-adventny-kalendar",
    "advent_article_3": "adventny-kalendar-mixit",
    "more_about_calendar": "Viac o adventnom kalendári",
    "read_full_article": "Čítať celý článok",
    "read_more_articles": "Čítať ďalšie články",
    "show_all": "Zobraziť všetky"
},
    "pl": {
    "advent_article_1": "kalendarz-adwentowy-mixit",
    "advent_article_2": "15_powodow",
    "advent_article_3": "kalendarz2025",
    "more_about_calendar": "Więcej o kalendarzu adwentowym",
    "read_full_article": "Przeczytaj cały artykuł",
    "read_more_articles": "Zobacz inne artykuły",
    "show_all": "Zobacz wszystkie"
},
    "hu": {
    "advent_article_1": "adventi-naptar-tortenete",
    "advent_article_2": "15_ok_advetni_naptar_mellett",
    "advent_article_3": "adventi_naptar_2025",
    "more_about_calendar": "Többet az adventi naptárról",
    "read_full_article": "Elolvasom a bejegyzést",
    "read_more_articles": "Következő bejegyzés",
    "show_all": "Mutass mindet"
},
    "de": {
    "advent_article_1": "adventskalender",
    "advent_article_2": "15-gruende-adventskalender",
    "advent_article_3": "adventskalender-2025",
    "more_about_calendar": "Mehr zum Adventskalender",
    "read_full_article": "Den ganzen Artikel lesen",
    "read_more_articles": "Weitere Artikel lesen",
    "show_all": "Alle anzeigen"
},
    "at": {
    "advent_article_1": "adventskalender",
    "advent_article_2": "15-gruende-adventskalender",
    "advent_article_3": "adventskalender-2025",
    "more_about_calendar": "Mehr zum Adventskalender",
    "read_full_article": "Den ganzen Artikel lesen",
    "read_more_articles": "Weitere Artikel lesen",
    "show_all": "Alle anzeigen"
},
    "ro": {
    "advent_article_1": "calendar-advent-mixit",
    "advent_article_2": "15-motive-pentru-calendarul-advent-mixit",
    "advent_article_3": "marele-calendar-advent-mixit-2025",
    "more_about_calendar": "Mai multe despre Calendarul de Advent",
    "read_full_article": "Citesc toată postarea",
    "read_more_articles": "Următoarea postare",
    "show_all": "Arată toate"
},
    "fr": {
    "advent_article_1": "calendrier-de-l'avent-mixit",
    "advent_article_2": "15-raisons-d'acheter-le-calendrier-de-l'avent",
    "advent_article_3": "grand-calendrier-de-l-avent",
    "more_about_calendar": "En savoir plus sur le calendrier de l’Avent",
    "read_full_article": "Lire l'article complet",
    "read_more_articles": "Lire d’autres articles",
    "show_all": "Afficher tout"
},
    "it": {
    "advent_article_1": "calendario-dell'avvento-mixit",
    "advent_article_2": "15-motivi-perche-calendario-dell'avvento",
    "advent_article_3": "grande-calendario-dell'avvento",
    "more_about_calendar": "Maggiori informazioni sul Calendario dell'Avvento",
    "read_full_article": "Leggi l'articolo completo",
    "read_more_articles": "Leggi altri articoli",
    "show_all": "Mostra tutto"
}
  }
</i18n>
